/**
 * AuditRiskEngine - Policy-led risk register with risk matrix
 * 
 * Features:
 *   - Risk register with entries classified by likelihood × consequence
 *   - Auto-calculated risk rating (Low/Medium/High/Extreme)
 *   - Control effectiveness tracking
 *   - Links to audit findings for evidence-based risk updates
 *   - Policy mapping and compliance tracking
 */

import React, { useState, useEffect } from 'react';
import {
  ShieldAlert, Plus, Search,
  TrendingDown, Edit2, Trash2,
  Save,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@shared/components/ui/card';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { Label } from '@shared/components/ui/label';
import { Textarea } from '@shared/components/ui/textarea';
import { Badge } from '@shared/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@shared/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@shared/components/ui/dialog';
import { toast } from '@shared/components/ui/use-toast';
import { supabase } from '@shared/supabaseClient';

const LIKELIHOOD = [
  { value: 'rare',           label: 'Rare',           score: 1, description: 'May occur only in exceptional circumstances' },
  { value: 'unlikely',       label: 'Unlikely',       score: 2, description: 'Could occur at some time' },
  { value: 'possible',       label: 'Possible',       score: 3, description: 'Might occur at some time' },
  { value: 'likely',         label: 'Likely',          score: 4, description: 'Will probably occur in most circumstances' },
  { value: 'almost_certain', label: 'Almost Certain',  score: 5, description: 'Expected to occur in most circumstances' },
];

const CONSEQUENCE = [
  { value: 'insignificant', label: 'Insignificant', score: 1, description: 'No injuries, low financial loss' },
  { value: 'minor',         label: 'Minor',         score: 2, description: 'First aid treatment, medium financial loss' },
  { value: 'moderate',      label: 'Moderate',       score: 3, description: 'Medical treatment required' },
  { value: 'major',         label: 'Major',          score: 4, description: 'Extensive injuries, major financial loss' },
  { value: 'catastrophic',  label: 'Catastrophic',    score: 5, description: 'Death, huge financial loss' },
];

function calculateRiskRating(likelihood, consequence) {
  const l = LIKELIHOOD.find(ll => ll.value === likelihood)?.score || 1;
  const c = CONSEQUENCE.find(cc => cc.value === consequence)?.score || 1;
  const score = l * c;
  if (score >= 15) return { level: 'extreme',  label: 'Extreme',  color: 'bg-red-600 text-white',    score };
  if (score >= 10) return { level: 'high',     label: 'High',     color: 'bg-orange-500 text-white',  score };
  if (score >= 5)  return { level: 'medium',   label: 'Medium',   color: 'bg-amber-400 text-black',   score };
  return              { level: 'low',      label: 'Low',      color: 'bg-green-400 text-black',   score };
}

const RISK_CATEGORIES = [
  'WHS', 'Environmental', 'Quality', 'Operational', 'Financial', 'Regulatory', 'Reputational', 'Strategic',
];

// ─── Risk Entry Editor ──────────────────────────────────────────────
function RiskEntryDialog({ open, onClose, entry, onSave }) {
  const [form, setForm] = useState({
    risk_title: '', description: '', category: 'WHS',
    likelihood: 'possible', consequence: 'moderate',
    existing_controls: '', additional_controls: '',
    risk_owner: '', review_date: '',
    residual_likelihood: '', residual_consequence: '',
    ...entry,
  });
  const [_saving, setSaving] = useState(false);

  useEffect(() => {
    if (entry) setForm(prev => ({ ...prev, ...entry }));
  }, [entry]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const inherentRisk = calculateRiskRating(form.likelihood, form.consequence);
  const residualRisk = form.residual_likelihood && form.residual_consequence
    ? calculateRiskRating(form.residual_likelihood, form.residual_consequence)
    : null;

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{entry?.id ? 'Edit Risk Entry' : 'New Risk Entry'}</DialogTitle>
          <DialogDescription>Assess and document risks using the likelihood × consequence matrix.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Risk Title *</Label>
            <Input value={form.risk_title} onChange={e => update('risk_title', e.target.value)}
              placeholder="e.g. Inadequate PPE compliance on site" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => update('category', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RISK_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Risk Owner</Label>
              <Input value={form.risk_owner} onChange={e => update('risk_owner', e.target.value)}
                placeholder="Person responsible" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={e => update('description', e.target.value)}
              placeholder="Describe the risk scenario..." rows={2} />
          </div>

          {/* Inherent Risk Assessment */}
          <Card className="border-l-4 border-l-red-400">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Inherent Risk (Before Controls)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Likelihood</Label>
                  <Select value={form.likelihood} onValueChange={v => update('likelihood', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LIKELIHOOD.map(l => (
                        <SelectItem key={l.value} value={l.value}>
                          <div>
                            <span className="font-medium">{l.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">({l.score})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Consequence</Label>
                  <Select value={form.consequence} onValueChange={v => update('consequence', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CONSEQUENCE.map(c => (
                        <SelectItem key={c.value} value={c.value}>
                          <div>
                            <span className="font-medium">{c.label}</span>
                            <span className="text-xs text-muted-foreground ml-2">({c.score})</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Risk Rating:</span>
                <Badge className={`${inherentRisk.color}`}>
                  {inherentRisk.label} ({inherentRisk.score})
                </Badge>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-1.5">
            <Label>Existing Controls</Label>
            <Textarea value={form.existing_controls} onChange={e => update('existing_controls', e.target.value)}
              placeholder="List current controls..." rows={2} />
          </div>

          <div className="space-y-1.5">
            <Label>Additional Controls Required</Label>
            <Textarea value={form.additional_controls} onChange={e => update('additional_controls', e.target.value)}
              placeholder="Additional measures needed..." rows={2} />
          </div>

          {/* Residual Risk Assessment */}
          <Card className="border-l-4 border-l-green-400">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Residual Risk (After Controls)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Likelihood</Label>
                  <Select value={form.residual_likelihood || ''} onValueChange={v => update('residual_likelihood', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {LIKELIHOOD.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Consequence</Label>
                  <Select value={form.residual_consequence || ''} onValueChange={v => update('residual_consequence', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {CONSEQUENCE.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {residualRisk && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Risk Rating:</span>
                  <Badge className={`${residualRisk.color}`}>{residualRisk.label} ({residualRisk.score})</Badge>
                  {residualRisk.score < inherentRisk.score && (
                    <span className="text-xs text-green-600 flex items-center gap-0.5">
                      <TrendingDown className="h-3 w-3" /> Reduced
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-1.5">
            <Label>Review Date</Label>
            <Input type="date" value={form.review_date} onChange={e => update('review_date', e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => {
            if (!form.risk_title?.trim()) {
              toast({ title: 'Required', description: 'Risk title is required', variant: 'destructive' });
              return;
            }
            onSave(form);
          }}>
            <Save className="h-4 w-4 mr-2" /> Save Risk
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Risk Engine ───────────────────────────────────────────────
export default function AuditRiskEngine({ user }) {
  const [risks, setRisks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [_categoryFilter, setCategoryFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [showEditor, setShowEditor] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);

  useEffect(() => { loadRisks(); }, []);

  const loadRisks = async () => {
    try {
      setIsLoading(true);
      // Using audit_findings as risk register entries (they have likelihood × consequence)
      const { data, error } = await supabase
        .from('audit_findings')
        .select(`
          *,
          audits ( audit_title, audit_reference )
        `)
        .not('likelihood', 'is', null)
        .order('identified_at', { ascending: false })
        .limit(100);

      if (error) throw error;
      setRisks(data || []);
    } catch (error) {
      console.error('Error loading risks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveRisk = async (form) => {
    try {
      const rating = calculateRiskRating(form.likelihood, form.consequence);

      if (form.id) {
        await supabase.from('audit_findings').update({
          finding_title: form.risk_title,
          finding_description: form.description || form.risk_title,
          likelihood: form.likelihood,
          consequence: form.consequence,
          risk_rating: rating.level,
        }).eq('id', form.id);
      } else {
        // Risk register entries stored as findings — need an audit_id
        // We'll look up the most recent audit for context, or use a placeholder approach
        await supabase.from('audit_findings').insert({
          finding_type: 'observation',
          finding_title: form.risk_title,
          finding_description: form.description || form.risk_title,
          likelihood: form.likelihood,
          consequence: form.consequence,
          risk_rating: rating.level,
          severity: rating.level === 'extreme' ? 'critical' : rating.level === 'high' ? 'high' : 'medium',
          status: 'open',
          identified_by: user.id,
          category: form.category || null,
        });
      }

      toast({ title: 'Risk Saved' });
      setShowEditor(false);
      setEditingRisk(null);
      loadRisks();
    } catch (error) {
      console.error('Error saving risk:', error);
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleDeleteRisk = async (risk) => {
    if (!confirm('Delete this risk entry?')) return;
    try {
      await supabase.from('audit_findings').delete().eq('id', risk.id);
      toast({ title: 'Risk Deleted' });
      loadRisks();
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  // Risk stats
  const riskCounts = { low: 0, medium: 0, high: 0, extreme: 0 };
  risks.forEach(r => {
    const rating = calculateRiskRating(r.likelihood, r.consequence);
    riskCounts[rating.level]++;
  });

  const filtered = risks.filter(r => {
    const rating = calculateRiskRating(r.likelihood, r.consequence);
    const matchesSearch = !search || r.finding_description?.toLowerCase().includes(search.toLowerCase());
    const matchesRating = ratingFilter === 'all' || rating.level === ratingFilter;
    return matchesSearch && matchesRating;
  });

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Risk Engine</h2>
          <p className="text-sm text-muted-foreground">
            Policy-led risk register with likelihood × consequence matrix
          </p>
        </div>
        <Button onClick={() => { setEditingRisk(null); setShowEditor(true); }}>
          <Plus className="h-4 w-4 mr-2" /> New Risk Entry
        </Button>
      </div>

      {/* Risk Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Extreme', count: riskCounts.extreme, color: 'text-red-600 bg-red-500/10' },
          { label: 'High', count: riskCounts.high, color: 'text-orange-600 bg-orange-500/10' },
          { label: 'Medium', count: riskCounts.medium, color: 'text-amber-600 bg-amber-500/10' },
          { label: 'Low', count: riskCounts.low, color: 'text-green-600 bg-green-500/10' },
        ].map(s => (
          <Card
            key={s.label}
            className={`p-3 cursor-pointer hover:border-amber-500/50 transition-colors ${ratingFilter === s.label.toLowerCase() ? 'border-amber-500' : ''}`}
            onClick={() => setRatingFilter(ratingFilter === s.label.toLowerCase() ? 'all' : s.label.toLowerCase())}
          >
            <div className={`text-2xl font-bold ${s.color.split(' ')[0]}`}>{s.count}</div>
            <div className="text-xs text-muted-foreground">{s.label} Risk</div>
          </Card>
        ))}
      </div>

      {/* Risk Matrix Visual */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 text-amber-500" />
            5×5 Risk Matrix
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr>
                  <th className="p-2 text-left text-muted-foreground w-28">Likelihood ↓</th>
                  {CONSEQUENCE.map(c => (
                    <th key={c.value} className="p-2 text-center text-muted-foreground text-[10px]">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...LIKELIHOOD].reverse().map(l => (
                  <tr key={l.value}>
                    <td className="p-2 font-medium text-[11px] text-muted-foreground">{l.label}</td>
                    {CONSEQUENCE.map(c => {
                      const rating = calculateRiskRating(l.value, c.value);
                      const count = risks.filter(r => r.likelihood === l.value && r.consequence === c.value).length;
                      return (
                        <td key={c.value} className="p-1">
                          <div className={`w-full h-10 rounded flex items-center justify-center font-bold ${rating.color}`}>
                            {count || ''}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search risks..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
      </div>

      {/* Risk Register */}
      {isLoading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <Card key={i} className="h-20 animate-pulse bg-muted/20" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <ShieldAlert className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground">No risk entries found</p>
          <Button variant="outline" className="mt-4" onClick={() => { setEditingRisk(null); setShowEditor(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Add First Risk
          </Button>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(risk => {
            const rating = calculateRiskRating(risk.likelihood, risk.consequence);
            return (
              <Card key={risk.id} className="hover:border-amber-500/50 transition-all group">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Badge className={`${rating.color} shrink-0`}>
                      {rating.label} ({rating.score})
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{risk.finding_description}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <span>L: {LIKELIHOOD.find(l => l.value === risk.likelihood)?.label}</span>
                        <span>×</span>
                        <span>C: {CONSEQUENCE.find(c => c.value === risk.consequence)?.label}</span>
                        {risk.audits?.audit_reference && (
                          <span className="font-mono ml-2">{risk.audits.audit_reference}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => {
                        setEditingRisk({
                          ...risk,
                          risk_title: risk.finding_description,
                        });
                        setShowEditor(true);
                      }}>
                        <Edit2 className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteRisk(risk)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <RiskEntryDialog
        open={showEditor}
        onClose={() => { setShowEditor(false); setEditingRisk(null); }}
        entry={editingRisk}
        onSave={handleSaveRisk}
      />
    </div>
  );
}
